import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface RebranchChatNameModalProps {
  defaultTitle: string;
  onConfirm: (title: string) => Promise<void>;
  onClose: () => void;
}

export function RebranchChatNameModal({
  defaultTitle,
  onConfirm,
  onClose,
}: RebranchChatNameModalProps) {
  const [title, setTitle] = useState(defaultTitle);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTitle(defaultTitle);
  }, [defaultTitle]);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(t);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create branch');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => !submitting && onClose()}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rebranch-modal-title"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 id="rebranch-modal-title" className="text-lg font-semibold text-slate-900">
            Rebranch chat
          </h2>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            disabled={submitting}
            className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-4 text-sm text-slate-600">
          This copies every message and your current context into a new chat. Name the new conversation,
          then you&apos;ll switch to it in the sidebar.
        </p>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <label className="mb-1 block text-xs font-medium text-slate-600">New chat name</label>
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-400"
            placeholder="Name for the branched chat"
            autoComplete="off"
            disabled={submitting}
          />
          {error && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg px-4 py-2 text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!title.trim() || submitting}
              className="rounded-lg bg-slate-900 px-4 py-2 text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 inline-flex items-center gap-2"
            >
              {submitting ? 'Creating…' : 'Create branch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
